import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { CloseIconBranding, ManagerHistoryIcon, SearchIcon } from "../../utils/icons";
import { fetchApi } from "../../utils/methods";

const CATEGORY_API = "/v1/getAllCategories";
const RECENT_KEY = "recent_category_searches";

const Wrapper = styled.div`
  position: relative;
  width: 360px;
  margin-right:20px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 36px 10px 12px;
  color:  #184A61;
  font-family: Lato;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  border-radius:${({active}) => active ? '8px 8px 0px 0px' : ' 8px'}; 
  border: 0.75px solid #DCDCDC;
  background: #FEFEFE;

  &:focus {
    outline: none;
  }
  &::placeholder {
    color:#BDBDBD;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

const Results = styled.div`
  border-radius: 0 0 8px 8px;
  border: 0.75px solid #DCDCDC;
  background: #FEFEFE;
  max-height: 280px;
  overflow-y: auto;
  position:absolute;
  width:100%;
  z-index:1;
  padding:4px 16px 4px 16px;
  ${'' /* top:87%; */}
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  margin:12px 0px;
  border: 0.75px solid transparent;

  &:hover {
    border-radius: 8px;
    border: 0.75px solid #DCDCDC;
    background: #F8F8F8;
  }
`;

const CategoryText = styled.span`
  color: #184A61;
  font-family: Lato;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
`;

const TypeTag = styled.span`
  ${'' /* margin-left: auto; */}
  color: #989898;
  font-family: Lato;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-transform:capitalize;
`;

const DeleteIcon = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    width: 10px;
    height: 10px;
  }
`;

export default function CategorySearchBar() {
  const history = useHistory();
  const wrapperRef = useRef(null);

  const [query, setQuery] = useState("");
  const [allCategories, setAllCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(false);


  // fetch categories once
  useEffect(() => {
    const getCategories = async () => {
      try {
        const res = await fetchApi(CATEGORY_API, "GET");
        setAllCategories(res?.data || []);
      } catch (e) {
        window.console.error(e);
      }
    };

    getCategories();

    const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    setRecent(stored);
  }, []);

  // frontend search
  const memoAllCategories = useMemo(() => allCategories, [allCategories]);
  const memoRecent = useMemo(() => recent, [recent]);
  
  useEffect(() => {
    if (!query.trim()) {
      setResults(memoRecent);
      return;
    }
  
    const q = query.toLowerCase();
  
    const startsWith = [];
    const includes = [];
  
    memoAllCategories.forEach((c) => {
      const title = c?.title?.toLowerCase() || "";
      const type = c?.type?.toLowerCase() || "";
  
      if (title.startsWith(q) || type.startsWith(q)) {
        startsWith.push(c);
      } else if (title.includes(q) || type.includes(q)) {
        includes.push(c);
      }
    });
  
    setResults([...startsWith, ...includes]);
  }, [query, memoAllCategories, memoRecent]);
  
  

  // close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const saveRecent = (item) => {
    const updated = [item, ...recent.filter((r) => r.id !== item.id)].slice(0, 5);
    setRecent(updated);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  };

  const isRecentItem = (item) => recent.some((r) => r.id === item.id);
  const deleteRecent = (e, itemId) => {
    e.stopPropagation(); // ⛔ stop redirect
    const updated = recent.filter((r) => r.id !== itemId);
    setRecent(updated);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  };

  const onSelect = (item) => {
    saveRecent(item);
    setOpen(false);
    history.push(`/wellness-resources/search?category=${item.title}&type=${item.type}&id=${item.id}`);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <InputWrapper>
        <Input
          placeholder="Search"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQuery(e.target.value)}
          active={results?.length && open}
        />
        <InputIcon>{SearchIcon('#DA6C74')}</InputIcon>
      </InputWrapper>

      {open && results?.length ? (
        <Results>
          {results.map((item) => {
            const isRecent = isRecentItem(item);
          
            return (
              <ResultItem key={item.id + item.title} onClick={() => onSelect(item)}>
                {/* LEFT ICON */}
                <span>{isRecent ? ManagerHistoryIcon('#184A61',16,16) : SearchIcon('#184A61')}</span>
          
                <CategoryText>{item?.title}</CategoryText>
          
                <TypeTag>{item?.type?.replace(/_/g, ' ')}</TypeTag>
          
                {/* DELETE ICON – only for recent */}
                {isRecent && (
                  <DeleteIcon onClick={(e) => deleteRecent(e, item.id)}>
                    {CloseIconBranding('#DA6C74')}
                  </DeleteIcon>
                )}
              </ResultItem>
            );
          })}
        </Results>
      ):null}
    </Wrapper>
  );
}
